/* import __COLOCATED_TEMPLATE__ from './pane.hbs'; */
/* RESPONSIBLE TEAM: team-inbox-management */
import { action } from '@ember/object';
import { inject as service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { type Block } from '@intercom/interblocks.ts';
import { useResource } from 'ember-resources';
import NavigableSelection from 'embercom/components/inbox2/common/navigable-selection-resource';
import { type CommandKPaneComponentArgs } from 'embercom/lib/inbox2/command-k-types';
import type ConvertToTicketAction from 'embercom/objects/inbox/command-k/convert-to-ticket';
import type Conversation from 'embercom/objects/inbox/conversation';
import { ConversationState } from 'embercom/objects/inbox/conversation';
import type LatestConversationSummary from 'embercom/objects/inbox/latest-conversation-summary';
import { type RenderableData } from 'embercom/objects/inbox/renderable-data';
import { DeduplicatedAsyncData } from 'embercom/resources/utils/async-data';
import type CommandKService from 'embercom/services/command-k';
import type InboxApi from 'embercom/services/inbox-api';
import type InboxState from 'embercom/services/inbox-state';
import type IntlService from 'embercom/services/intl';
import type Session from 'embercom/services/session';
import type Snackbar from 'embercom/services/snackbar';
import { type ConversationRecord } from 'embercom/objects/inbox/types/conversation-record';
import PredicateGroup, {
  type Predicate,
  type SimplePredicate,
} from 'embercom/objects/inbox/search/predicate-group';
import { type SortParams } from 'embercom/services/inbox-api';
import type ConversationTableEntry from 'embercom/objects/inbox/conversation-table-entry';
import { cancel, debounce } from '@ember/runloop';
import { Channel, CHAT_CHANNELS } from 'embercom/models/data/inbox/channels';

interface Signature {
  Args: CommandKPaneComponentArgs<ConvertToTicketAction>;
}

interface renderableDataType extends RenderableData {
  blocks?: Block[] | undefined;
}

const CROSS_USER_MERGEABLE_CHANNELS = [Channel.Email, Channel.PhoneCall];

export default class MergeIntoPaneComponent extends Component<Signature> {
  @service declare commandK: CommandKService;
  @service declare intl: IntlService;
  @service declare inboxState: InboxState;
  @service declare inboxApi: InboxApi;
  @service declare intercomEventService: any;
  @service declare session: Session;
  @service declare snackbar: Snackbar;

  @tracked query = '';
  @tracked isPreviewModalOpen = false;
  @tracked isMergeConfirmationModalOpen = false;

  searchDebounce?: any;

  selection = useResource(this, NavigableSelection, () => ({
    items: this.latestConversations,
    isCommandKVisible: this.commandK.isVisible,
  }));

  get conversation() {
    return this.inboxState.activeConversation as Conversation;
  }

  get isCrossUserMergeEnabled(): boolean {
    return this.session.workspace.enabledCrossUserConversationMerge;
  }

  get latestConversations() {
    if (this.useCrossUserSearch) {
      // When the teammate hasn't search for anything, show the latest conversations for the
      // current user, and if there are less than 5, add the results of the cross-user search.
      if (!this.query) {
        let latestConversations = this.latestConversationsLoader.value || [];

        if (latestConversations.length < 5) {
          let crossUserConversations = this.latestConversationsLoaderCrossUser.value ?? [];
          return [
            ...latestConversations,
            ...crossUserConversations.filter(
              (conversation: LatestConversationSummary) =>
                !latestConversations.some(
                  (c: LatestConversationSummary) => c.id === conversation.id,
                ),
            ),
          ];
        } else {
          return latestConversations;
        }
      } else {
        return this.latestConversationsLoaderCrossUser.value ?? [];
      }
    } else {
      let latestConversations = this.latestConversationsLoader.value;

      if (!latestConversations) {
        return [];
      }

      let filteredConversations = this.query
        ? this.sortLatestConversations(latestConversations).filter((conversation) =>
            this.isConversationMatchQuery(conversation, this.query),
          )
        : this.sortLatestConversations(latestConversations);

      return filteredConversations;
    }
  }

  get useCrossUserSearch() {
    let crossUserMergeableChannels = [...CROSS_USER_MERGEABLE_CHANNELS, ...CHAT_CHANNELS];

    return (
      this.isCrossUserMergeEnabled &&
      crossUserMergeableChannels.includes(this.conversation?.channel?.initial)
    );
  }

  @action hide() {
    this.commandK.hide();
    this.isPreviewModalOpen = false;
    this.isMergeConfirmationModalOpen = false;
    this.intercomEventService.trackAnalyticsEvent({
      action: 'exit',
      object: 'commandk_merge_modal',
      section: 'composer',
    });
  }

  @action onShowMergeConfirmationModal() {
    this.isPreviewModalOpen = false;
    this.isMergeConfirmationModalOpen = true;
    this.intercomEventService.trackAnalyticsEvent({
      action: 'show',
      object: 'confirmation_merge_preview_modal_warning',
      section: 'conversation_merge',
    });
  }

  @action onCloseMergeConfirmationModal() {
    this.isMergeConfirmationModalOpen = false;
    this.isPreviewModalOpen = true;
  }

  @action onCloseAllModals() {
    this.commandK.hide();
    this.isMergeConfirmationModalOpen = false;
    this.isPreviewModalOpen = false;
  }

  private isConversationMatchQuery(
    conversation: LatestConversationSummary,
    query: string,
  ): boolean {
    let lowerCaseTitle = conversation.title?.toLowerCase() || '';
    let lowerCaseQuery = query.toLowerCase();
    let renderableData = conversation.lastRenderableSummaryPart
      ?.renderableData as renderableDataType;
    let blockData: any = renderableData?.blocks?.firstObject;
    let conversationId = conversation.id.toString();
    let ticketId = conversation.ticketId?.toString();
    let textToSearch = (blockData && blockData.text?.toLowerCase()) || '';
    return (
      textToSearch.includes(lowerCaseQuery) ||
      lowerCaseTitle.includes(lowerCaseQuery) ||
      conversationId.includes(lowerCaseQuery) ||
      ticketId?.includes(lowerCaseQuery)
    );
  }

  get limitedLatestConversations() {
    if (this.isCrossUserMergeEnabled) {
      return this.latestConversations;
    } else {
      return this.latestConversations.slice(0, 5);
    }
  }

  latestConversationsLoader = DeduplicatedAsyncData(
    this,
    () => [
      this.conversation?.participantSummaries?.length === 1
        ? this.conversation.participantSummaries[0].id
        : this.conversation?.user.id,
      this.conversation?.id,
    ],
    async (userID, conversationID, { signal }) => {
      let { conversations } = await this.inboxApi.getUserConversations(userID, conversationID, {
        signal,
      });

      return conversations.filter((convo) => {
        return (
          convo.participantSummaries.length <= 1 &&
          !convo.redacted &&
          (this.conversation.isTicket ? convo.isTicket : true) &&
          this.isSameSlackChannel(convo) &&
          this.isSameDiscordChannel(convo)
        );
      });
    },
  );

  private getParticipantIds(conversation: Conversation | LatestConversationSummary): Set<string> {
    return new Set(conversation.participantSummaries?.map((p) => p.id) ?? []);
  }

  get hasDifferentParticipants(): boolean {
    if (!this.selection.selected) {
      return false;
    }

    let primaryIds = this.getParticipantIds(this.selection.selected);
    let secondaryIds = this.getParticipantIds(this.conversation);

    // If either conversation has no participants, treat them as different
    if (primaryIds.size === 0 || secondaryIds.size === 0) {
      return true;
    }

    // Check if the sets have different sizes or any non-matching elements
    return (
      primaryIds.size !== secondaryIds.size || [...primaryIds].some((id) => !secondaryIds.has(id))
    );
  }

  /**
   * Determines if we should swap the merge order for WhatsApp conversations.
   * WhatsApp conversations that are closed cannot be re-opened, so the open
   * conversation must be the primary (target) to preserve reply capability.
   */
  get shouldSwapForWhatsApp(): boolean {
    let selected = this.selection.selected;
    if (!selected) {
      return false;
    }

    // Check if active conversation is WhatsApp (which means selected must also be WhatsApp)
    if (this.conversation.channel?.initial !== Channel.Whatsapp) {
      return false;
    }

    // Swap if: selected (would-be primary) is CLOSED and active (would-be secondary) is OPEN
    // We want: primary = open, secondary = closed
    let selectedIsClosed = selected.state === ConversationState.Closed;
    let activeIsOpen = this.conversation.state === ConversationState.Open;

    return selectedIsClosed && activeIsOpen;
  }

  get actualPrimaryConversation(): ConversationRecord {
    if (this.shouldSwapForWhatsApp) {
      return this.conversation; // Active becomes primary (it's open)
    }
    return this.selection.selected;
  }

  get actualSecondaryConversation(): ConversationRecord {
    if (this.shouldSwapForWhatsApp) {
      return this.selection.selected; // Selected becomes secondary (it's closed)
    }
    return this.conversation;
  }

  latestConversationsLoaderCrossUser = DeduplicatedAsyncData(
    this,
    () => [this.query, this.conversation?.channel?.initial],
    async (query, conversationInitialChannel) => {
      // Define which channels can be merged based on the initial channel
      let allowedChannels: Channel[] = [];
      // Email --- can merge into -> [Email, Chat]
      if (conversationInitialChannel === Channel.Email) {
        allowedChannels = [Channel.Email, ...CHAT_CHANNELS];
      } else if (CHAT_CHANNELS.includes(conversationInitialChannel)) {
        // Chat --- can merge into -> [Email, Chat]
        allowedChannels = [Channel.Email, ...CHAT_CHANNELS];
      } else if (conversationInitialChannel === Channel.PhoneCall) {
        // PhoneCall -> [Email, Chat, PhoneCall]
        allowedChannels = [Channel.Email, ...CHAT_CHANNELS, Channel.PhoneCall];
      }

      // Create a predicate for allowed channels
      let predicates: Predicate[] = [
        {
          identifier: 'initial-channel',
          type: 'or',
          predicates: allowedChannels.map(
            (channel) =>
              ({
                identifier: `initial-channel`,
                type: 'integer',
                attribute: 'initial_channel',
                comparison: 'eq',
                value: channel,
              }) as SimplePredicate,
          ),
        },
      ];

      // If the query is a number, search conversation and ticket ids
      if (query && !isNaN(parseInt(query, 10))) {
        predicates.push({
          identifier: 'conversation-or-ticket-id',
          type: 'or',
          predicates: [
            {
              identifier: 'id',
              type: 'id',
              attribute: 'id',
              comparison: 'eq',
              value: query,
            } as SimplePredicate,
            {
              identifier: 'ticket-id',
              type: 'id',
              attribute: 'ticket_id',
              comparison: 'eq',
              value: query,
            } as SimplePredicate,
          ],
        });
      }

      let predicatesGroup = new PredicateGroup(predicates);

      let searchParams = {
        query,
        sortParams: {
          sort_field: 'relevance',
          sort_direction: 'desc',
        } as SortParams,
        count: 10,
        predicates: { predicates: predicatesGroup.predicates },
        fields: [
          'last_renderable_part',
          'ticket_type',
          'ticket_id',
          'ticket_custom_state_id',
          'title',
          'last_updated',
        ],
        searchSource: 'merge-into-command-k',
      };
      let { conversations } = await this.inboxApi.searchForConversationsTableV2(searchParams);
      let conversationsExcludingCurrent = conversations.filter(
        (conversation: ConversationTableEntry) => conversation.id !== this.conversation.id,
      );
      return conversationsExcludingCurrent;
    },
  );

  private sortLatestConversations(
    conversations: LatestConversationSummary[],
  ): LatestConversationSummary[] {
    return [...conversations].sort(
      (conv1: LatestConversationSummary, conv2: LatestConversationSummary) =>
        conv2.lastUpdated.getTime() - conv1.lastUpdated.getTime(),
    );
  }

  @action search(query: string) {
    if (this.searchDebounce) {
      cancel(this.searchDebounce);
    }
    this.searchDebounce = debounce(this, this.setQuery, query, 150);

    this.commandK.trackSearch({
      query: this.query,
      number_of_results: this.latestConversations.length,
    });
  }

  setQuery(query: string) {
    this.query = query;
  }

  @action async mergeConversation(
    primaryConversationSummary: ConversationRecord,
    secondaryConversationSummary: ConversationRecord,
  ) {
    await this.inboxApi.mergeConversation(
      secondaryConversationSummary.id,
      primaryConversationSummary.id,
    );
    let { isTicket, title, id, ticketId } = primaryConversationSummary;

    let type = isTicket
      ? this.intl.t('inbox.merge-into.ticket-type')
      : this.intl.t('inbox.merge-into.conversation-type');

    this.intercomEventService.trackAnalyticsEvent({
      action: 'confirm',
      object: 'confirmation_merge_preview_modal',
      section: 'conversation_merge',
    });

    this.snackbar.notify(
      this.intl.t('inbox.merge-into.merged-conversation', {
        type,
        title,
        id: isTicket ? ticketId : id,
      }),
    );
  }

  private isSameSlackChannel(conversation: LatestConversationSummary): boolean {
    if (!this.conversation.channel || this.conversation.channel.current !== Channel.Slack) {
      return true;
    }

    let currentSlackChannelId = this.conversation.getAttributeForDescriptor('slack_channel')?.value;
    if (!currentSlackChannelId) {
      return true;
    }

    let slackChannelId = conversation.getAttributeForDescriptor('slack_channel')?.value;
    return slackChannelId === currentSlackChannelId;
  }

  private isSameDiscordChannel(conversation: LatestConversationSummary): boolean {
    if (!this.conversation.channel || this.conversation.channel.current !== Channel.Discord) {
      return true;
    }

    let currentDiscordChannelId =
      this.conversation.getAttributeForDescriptor('discord_channel')?.value;
    if (!currentDiscordChannelId) {
      return true;
    }

    let discordChannelId = conversation.getAttributeForDescriptor('discord_channel')?.value;
    return discordChannelId === currentDiscordChannelId;
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Inbox2::CommandK::MergeInto::Pane': typeof MergeIntoPaneComponent;
    'inbox2/command-k/merge-into/pane': typeof MergeIntoPaneComponent;
  }
}
